/**
 * 获取节点锚点。
 *
 * @param {Object} position 节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @param {string} side 边方向
 * @param {string} slot 槽位
 * @returns {{x:number,y:number}}
 */
export function getFlowNodeAnchor(position, nodeWidth, nodeHeight, side, slot) {
  if (!position) {
    return { x: 0, y: 0 };
  }
  const left = position.x - nodeWidth / 2;
  const right = position.x + nodeWidth / 2;
  const top = position.y - nodeHeight / 2;
  const bottom = position.y + nodeHeight / 2;
  const offsetMap = {
    oneFifth: nodeWidth / 5,
    twoFifths: (nodeWidth * 2) / 5,
    threeFifths: (nodeWidth * 3) / 5,
    fourFifths: (nodeWidth * 4) / 5,
    oneThird: nodeWidth / 3,
    twoThirds: (nodeWidth * 2) / 3,
    center: nodeWidth / 2,
  };
  const horizontalOffset = offsetMap[slot] || offsetMap.center;
  const verticalOffset =
    {
      oneFifth: nodeHeight / 5,
      twoFifths: (nodeHeight * 2) / 5,
      threeFifths: (nodeHeight * 3) / 5,
      fourFifths: (nodeHeight * 4) / 5,
      oneThird: nodeHeight / 3,
      twoThirds: (nodeHeight * 2) / 3,
      center: nodeHeight / 2,
    }[slot] || nodeHeight / 2;

  if (side === "top") {
    return {
      x: left + horizontalOffset,
      y: top,
    };
  }
  if (side === "bottom") {
    return {
      x: left + horizontalOffset,
      y: bottom,
    };
  }
  if (side === "left") {
    return {
      x: left,
      y: top + verticalOffset,
    };
  }
  return {
    x: right,
    y: top + verticalOffset,
  };
}

/**
 * 获取子流程返回线起点锚点。
 *
 * @param {Object} position 节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @returns {{x:number,y:number}}
 */
export function getSubProcessReturnSourceAnchor(position, nodeWidth, nodeHeight) {
  return getFlowNodeAnchor(
    position,
    nodeWidth,
    nodeHeight,
    "bottom",
    "twoThirds"
  );
}

/**
 * 获取子流程发起线折点。
 *
 * @param {Object} fromPos 源节点中心点
 * @param {Object} toPos 目标节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @param {string} slot 槽位
 * @returns {{start:Object,corner:Object,end:Object}}
 */
export function getSubProcessLaunchRoute(
  fromPos,
  toPos,
  nodeWidth,
  nodeHeight,
  slot = "oneFifth"
) {
  const start = getFlowNodeAnchor(fromPos, nodeWidth, nodeHeight, "top", slot);
  const endSide = toPos.x < fromPos.x ? "right" : "left";
  const end = getFlowNodeAnchor(toPos, nodeWidth, nodeHeight, endSide, "center");
  return {
    start,
    corner: {
      x: start.x,
      y: end.y,
    },
    end,
  };
}

/**
 * 获取子流程发起驳回线折点。
 *
 * @param {Object} fromPos 源节点中心点
 * @param {Object} toPos 目标节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @returns {{start:Object,corner:Object,end:Object}}
 */
export function getSubProcessLaunchRejectRoute(
  fromPos,
  toPos,
  nodeWidth,
  nodeHeight
) {
  const startSide = toPos.x < fromPos.x ? "left" : "right";
  const start = getFlowNodeAnchor(
    fromPos,
    nodeWidth,
    nodeHeight,
    startSide,
    "center"
  );
  const end = getFlowNodeAnchor(
    toPos,
    nodeWidth,
    nodeHeight,
    "top",
    "twoFifths"
  );
  return {
    start,
    corner: {
      x: end.x,
      y: start.y,
    },
    end,
  };
}

/**
 * 获取子流程返回线折点。
 *
 * @param {Object} fromPos 源节点中心点
 * @param {Object} toPos 目标节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @param {string} slot 槽位
 * @returns {{start:Object,corner:Object,end:Object}}
 */
export function getSubProcessReturnRoute(
  fromPos,
  toPos,
  nodeWidth,
  nodeHeight,
  slot = "threeFifths"
) {
  const start = getSubProcessReturnSourceAnchor(fromPos, nodeWidth, nodeHeight);
  const end = getFlowNodeAnchor(toPos, nodeWidth, nodeHeight, "top", slot);
  return {
    start,
    corner: {
      x: end.x,
      y: start.y,
    },
    end,
  };
}

/**
 * 获取子流程返回驳回线折点。
 *
 * @param {Object} fromPos 源节点中心点
 * @param {Object} toPos 目标节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @returns {{start:Object,corner:Object,end:Object}}
 */
export function getSubProcessReturnRejectRoute(
  fromPos,
  toPos,
  nodeWidth,
  nodeHeight
) {
  const start = getFlowNodeAnchor(
    fromPos,
    nodeWidth,
    nodeHeight,
    "right",
    "oneThird"
  );
  const end = getFlowNodeAnchor(
    toPos,
    nodeWidth,
    nodeHeight,
    "top",
    "twoFifths"
  );
  return {
    start,
    corner: {
      x: end.x,
      y: start.y,
    },
    end,
  };
}

/**
 * 获取子流程内部边的基础路由。
 *
 * @param {Object} fromPos 源节点中心点
 * @param {Object} toPos 目标节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @returns {{start:Object,end:Object,routeType:string}}
 */
export function getSubProcessEdgeRoute(fromPos, toPos, nodeWidth, nodeHeight) {
  const verticalAligned = Math.abs(fromPos.x - toPos.x) < 5;
  const upward = toPos.y < fromPos.y;
  const startSide = verticalAligned ? (upward ? "top" : "bottom") : "right";
  const endSide = verticalAligned ? (upward ? "bottom" : "top") : "left";
  const start = getFlowNodeAnchor(
    fromPos,
    nodeWidth,
    nodeHeight,
    startSide,
    "center"
  );
  const end = getFlowNodeAnchor(
    toPos,
    nodeWidth,
    nodeHeight,
    endSide,
    "center"
  );

  if (verticalAligned) {
    return {
      start,
      end,
      routeType: "vertical",
    };
  }

  return {
    start,
    end,
    routeType: "horizontal",
  };
}

/**
 * 获取主流程基础路由。
 *
 * @param {Object} fromPos 源节点中心点
 * @param {Object} toPos 目标节点中心点
 * @param {number} nodeWidth 节点宽度
 * @param {number} nodeHeight 节点高度
 * @returns {{start:Object,end:Object,routeType:string}}
 */
export function getMainFlowEdgeRoute(fromPos, toPos, nodeWidth, nodeHeight) {
  const verticalDiff = toPos.y - fromPos.y;
  const sameLevel = Math.abs(verticalDiff) < 5;
  if (sameLevel) {
    return {
      start: getFlowNodeAnchor(
        fromPos,
        nodeWidth,
        nodeHeight,
        "right",
        "center"
      ),
      end: getFlowNodeAnchor(toPos, nodeWidth, nodeHeight, "left", "center"),
      routeType: "straight",
    };
  }

  if (verticalDiff < 0) {
    return {
      start: getFlowNodeAnchor(
        fromPos,
        nodeWidth,
        nodeHeight,
        "right",
        "center"
      ),
      end: getFlowNodeAnchor(
        toPos,
        nodeWidth,
        nodeHeight,
        "bottom",
        "center"
      ),
      routeType: "upCross",
    };
  }

  return {
    start: getFlowNodeAnchor(
      fromPos,
      nodeWidth,
      nodeHeight,
      "bottom",
      "center"
    ),
    end: getFlowNodeAnchor(toPos, nodeWidth, nodeHeight, "left", "center"),
    routeType: "downCross",
  };
}
